import { ExternalLink, Text } from "@clipboard-health/ui-react";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Chip, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { CopyableText } from "@src/appV2/CopyableText";
import { useToast } from "@src/appV2/lib";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { getLabel, getQualificationLabel } from "@src/appV2/PlacementCandidates/utils/helper";
import { Icon } from "@src/appV2/redesign/components/Icon";
import { capitalize } from "lodash";

import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";
import { useGetHiringUrl } from "../hooks/useGetHiringUrl";
import { usePlacementPerks } from "../hooks/usePlacementPerks";
import { PLACEMENT_STATUS } from "../types/createPlacement.schema";
import { formatFixedDays, formatFlexibleSchedule } from "../utils/formatScheduleDays";
import { formatPayRange, getStatusColor } from "../utils/placementDetails";
import { formatTagsByGroup } from "../utils/placementTags";
import { ReadMore } from "./ReadMore";

interface PlacementDetailsProps {
  placement: PlacementWithDetails;
  children?: React.ReactNode;
}

export function PlacementDetails(props: PlacementDetailsProps) {
  const { placement, children } = props;
  const { admin } = useSession();
  const placementPerks = usePlacementPerks();
  const placementStatusLabel =
    PLACEMENT_STATUS.find((status) => status.value === placement.status)?.label ?? "Unknown";

  const { showSuccessToast } = useToast();
  const { isEnabled: isCopyHiringUrlEnabled, getPlacementHiringUrl } = useGetHiringUrl();

  return (
    <Stack direction="row" gap={3} width="100%">
      <Stack flex={2} justifyContent="flex-start" alignItems="flex-start" spacing={1}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Text semibold variant="body1">
            {placement.title}
          </Text>
          <Chip
            label={placementStatusLabel}
            color={getStatusColor(placement.status)}
            size="small"
            variant="outlined"
          />
        </Stack>

        <Stack direction="row" gap={0.5} alignItems="center">
          <Icon type="identity-card" size="small" />
          <Text variant="body2">{placement.workerTypes.map(getQualificationLabel).join(", ")}</Text>
        </Stack>
        {formatTagsByGroup(placement.tags).map((tagGroup, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Text key={index} variant="body2" color="text.primary">
            {tagGroup.groupLabel}: <b>{tagGroup.formattedTags}</b>
          </Text>
        ))}

        {placement.description && (
          <ReadMore
            id="placement-description"
            text={placement.description}
            variant="body2"
            color="text.secondary"
          />
        )}

        {admin && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <AutoFixNormalIcon sx={{ fontSize: 16 }} />
            <Text variant="body2">Tier: {capitalize(placement.tier)}</Text>
          </Box>
        )}

        {placement.hiringManagers?.map((hiringManager) => (
          <Box key={hiringManager.email} display="flex" alignItems="center" gap={0.5}>
            <PersonIcon sx={{ fontSize: 16 }} />
            <Text variant="body2">
              Contact: {hiringManager.name}, {hiringManager.email}
            </Text>
          </Box>
        ))}
        {placement.originalJobLink && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <OpenInNewIcon sx={{ fontSize: 16 }} />
            <Text variant="body2">
              Original at: <ExternalLink to={placement.originalJobLink}>Link</ExternalLink>
            </Text>
          </Box>
        )}

        {placement.jobShifts
          .filter((jobShift) => jobShift.enabled)
          .map((jobShift, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Stack key={index} direction="row" gap={1} flexShrink={0} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={0.5}>
                <Icon type="briefcase-outlined" size="small" />
                <Text variant="body2">{getLabel(jobShift.jobType, "jobType")}</Text>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Icon type="clock-outlined" size="small" />
                <Text variant="body2">
                  {jobShift.shiftTypes
                    .map((shiftType) => getLabel(shiftType, "shiftType"))
                    .join(", ")}
                </Text>
              </Box>
              {jobShift.scheduleType === "flexible" && jobShift.minimumRequiredDaysPerWeek && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Icon type="calendar" size="small" />
                  <Text variant="body2">
                    {formatFlexibleSchedule(
                      jobShift.minimumRequiredDaysPerWeek,
                      jobShift.workdaysRequired
                    )}
                  </Text>
                </Box>
              )}
              {jobShift.scheduleType === "4_on_2_off" && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Icon type="calendar" size="small" />
                  <Text variant="body2">4 days on, 2 days off</Text>
                </Box>
              )}
              {jobShift.scheduleType === "fixed" && jobShift.daysOfWeek.length > 0 && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Icon type="calendar" size="small" />
                  <Text variant="body2">{formatFixedDays(jobShift.daysOfWeek)}</Text>
                </Box>
              )}
              {jobShift.scheduleType === "other" && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Icon type="calendar" size="small" />
                  <Text variant="body2">Other</Text>
                </Box>
              )}
              {!jobShift.scheduleType && jobShift.daysOfWeek.length > 0 && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Icon type="calendar" size="small" />
                  <Text variant="body2">{formatFixedDays(jobShift.daysOfWeek)}</Text>
                </Box>
              )}
            </Stack>
          ))}

        <Box display="flex" alignItems="center" gap={0.5}>
          <Icon type="building" size="medium" />
          <Text variant="body2">{placement.workplace.name}</Text>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Icon type="map-pin" size="small" />
          <Text variant="body2">{placement.address.formatted}</Text>
        </Box>

        {(placement.signOnBonus ?? placement.perks.length > 0) && (
          <Stack direction="row" flexWrap="wrap" gap={1} flexShrink={0}>
            {placement.signOnBonus && (
              <Chip
                variant="outlined"
                color="primary"
                size="small"
                label={`Sign-on bonus: ${formatDollarsAsUsd(
                  placement.signOnBonus.amountInMinorUnits / 100
                )}`}
              />
            )}
            {placement.perks.map((perk) => {
              const perkData = placementPerks.find((p) => p.value === perk);
              return (
                perkData && (
                  <Chip key={perk} label={perkData.label} size="small" variant="outlined" />
                )
              );
            })}
          </Stack>
        )}

        {isCopyHiringUrlEnabled ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <CopyableText
              textVariant="body2"
              value="Job Application URL"
              copyableText={getPlacementHiringUrl(placement.id)}
              rootStackSx={{
                "& .MuiTypography-root": {
                  color: "info.main",
                },
              }}
              onCopyClick={() => {
                showSuccessToast("Placement hiring URL copied to clipboard");
              }}
            />
          </Box>
        ) : undefined}
      </Stack>

      <Stack
        flexDirection="column"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "flex-end" }}
        gap={1}
        flex={1}
      >
        {children}
        <Text variant="subtitle1" color="primary">
          {formatPayRange(placement.payRange)}
        </Text>
      </Stack>
    </Stack>
  );
}
